import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/client';
import SponsorBanner from '../components/SponsorBanner';
import EventDetail from './EventDetail';
import logo from '../logo.png';

export default function Home() {
  const [currentEvent, setCurrentEvent] = useState(null);
  const [loadingEvent, setLoadingEvent] = useState(true);
  const videoRef = useRef(null);

  useEffect(() => {
    api.get('/api/events/current')
      .then((r) => setCurrentEvent(r.data))
      .catch(() => setCurrentEvent(null))
      .finally(() => setLoadingEvent(false));
  }, []);

  return (
    <div>
      {/* ── Hero mit Video-Hintergrund ── */}
      <div
        className="relative flex flex-col items-center justify-center text-center overflow-hidden -mx-3 sm:-mx-6 -mt-5 sm:-mt-8"
        style={{ minHeight: 'calc(100vh - 64px)' }}
      >
        {/* Looping background video */}
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover"
          src="/background.mp4"
          autoPlay
          muted
          loop
          playsInline
        />

        {/* Dunkle Überlagerung */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50" />

        {/* Hero-Inhalt */}
        <div className="relative z-10 px-4 py-16 sm:py-24 flex flex-col items-center max-w-3xl mx-auto">
          <img
            src={logo}
            alt="BeachLAN Hörup"
            className="h-24 sm:h-36 w-auto object-contain mb-6 drop-shadow-2xl"
          />

          <h1 className="text-4xl sm:text-6xl font-black text-white tracking-tight leading-none mb-3 drop-shadow-lg">
            Beach<span className="text-sand-400">LAN</span>
          </h1>

          <p className="text-ocean-200 text-base sm:text-xl font-medium tracking-widest uppercase mb-2 drop-shadow">
            Gaming · Community · Sommer
          </p>
          <p className="text-white/60 text-sm sm:text-base mb-10 max-w-md leading-relaxed">
            Das LAN-Party Erlebnis in Hörup – genieße spannende Turniere,
            triff Gleichgesinnte und erlebe unvergessliche Gaming-Nächte.
          </p>

          <div className="flex gap-4 flex-wrap justify-center">
            <a
              href="#aktuelles-event"
              className="no-underline inline-flex items-center gap-2 bg-white text-ocean-900 font-bold px-7 py-3 rounded-lg hover:bg-ocean-50 transition-all shadow-lg text-sm sm:text-base hover:scale-105 active:scale-95"
            >
              Zum aktuellen Event
            </a>
            <Link
              to="/register"
              className="no-underline inline-flex items-center gap-2 bg-sand-500 text-white font-bold px-7 py-3 rounded-lg hover:bg-sand-600 transition-all shadow-lg text-sm sm:text-base hover:scale-105 active:scale-95"
            >
              Jetzt registrieren
            </Link>
          </div>
        </div>

        {/* Scroll-Indicator */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/40 flex flex-col items-center gap-1 animate-bounce">
          <span className="text-xs tracking-widest uppercase">Scroll</span>
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* ── Features ── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-slate-200 rounded-xl overflow-hidden mt-8 shadow-md">
        {[
          { icon: '🎮', label: 'Hardcore Gaming', desc: 'LAN-Spielspaß mit Gleichgesinnten – von Early Bird bis Sunrise.' },
          { icon: '🏆', label: 'Turniere', desc: 'Kämpfe in Single- oder Double-Elimination-Brackets um den Titel.' },
          { icon: '🌊', label: 'Beach-Vibes', desc: 'Sonne, Sand und Pixelschlachten – das gibt es nur bei uns.' },
        ].map((f) => (
          <div key={f.label} className="bg-white px-6 py-6 hover:bg-ocean-50 transition-colors">
            <div className="text-3xl mb-2">{f.icon}</div>
            <p className="text-xs font-black uppercase tracking-widest text-ocean-600 mb-1">{f.label}</p>
            <p className="text-sm text-slate-500 leading-relaxed">{f.desc}</p>
          </div>
        ))}
      </div>

      {/* ── Aktuelles Event ── */}
      <div id="aktuelles-event" className="mt-12 scroll-mt-20">
        {loadingEvent ? (
          <div className="flex justify-center py-14">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-ocean-400 border-t-transparent" />
          </div>
        ) : currentEvent ? (
          <EventDetail eventId={currentEvent.id} embedded />
        ) : (
          <div className="card text-center py-14">
            <p className="text-slate-400 text-sm mb-3">Aktuell ist kein Event aktiv — schau bald wieder vorbei.</p>
            <Link to="/archiv" className="text-sm font-semibold text-ocean-600 hover:text-ocean-800 no-underline">
              Vergangene Events im Archiv ansehen →
            </Link>
          </div>
        )}
      </div>
      {/* ── Sponsoren ── */}
      {currentEvent && <SponsorBanner global />}
    </div>
  );
}
